import type { JSX } from 'react';
import { useT } from '../i18n/useT';
import type { BookingDraft } from './bookingForm';
import { Field } from './FieldError';
import { PhoneField } from './PhoneField';

interface StepProps {
  draft: BookingDraft;
  errors: Record<string, string>;
  onChange: (patch: Partial<BookingDraft>) => void;
}

/** The owner as the customer on the order, never the person staying. */
export const BillingStep = ({ draft, errors, onChange }: StepProps): JSX.Element => {
  const t = useT();
  const field = (
    name: 'street' | 'postcode' | 'city',
    label: string,
    extra: { autoComplete: string; inputMode?: 'numeric' }
  ): JSX.Element => (
    <Field label={label} error={errors[name]}>
      {(a11y) => (
        <input
          {...a11y}
          className="ow-field"
          type="text"
          inputMode={extra.inputMode}
          autoComplete={extra.autoComplete}
          value={draft[name]}
          onChange={(e) => {
            onChange({ [name]: e.target.value });
          }}
        />
      )}
    </Field>
  );
  return (
    <section className="ow-form" aria-labelledby="ow-details-heading">
      <h3 id="ow-details-heading" className="ow-step-heading" tabIndex={-1} data-step-heading>
        {t('Your details')}
      </h3>
      <p className="ow-info">
        {t('This is you as the owner, not the person staying. We save it for next time.')}
      </p>
      {field('street', t('Address'), { autoComplete: 'street-address' })}
      {field('postcode', t('Postcode'), { autoComplete: 'postal-code', inputMode: 'numeric' })}
      {field('city', t('City'), { autoComplete: 'address-level2' })}
      <PhoneField
        label={t('Phone number')}
        country={draft.billingCountry}
        number={draft.billingPhone}
        error={errors['billingPhone']}
        onChange={(patch) => {
          onChange({
            ...(patch.country === undefined ? {} : { billingCountry: patch.country }),
            ...(patch.number === undefined ? {} : { billingPhone: patch.number })
          });
        }}
      />
    </section>
  );
};
